// src/modules/ai-agent/IvrMenuManager.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../layouts/DashboardLayout';
import Modal from '../../components/shared/Modal';
import { getIvrMenu, createIvrMenuOption, updateIvrMenuOption, deleteIvrMenuOption } from './aiAgentService';
import { FaPhone, FaPlus, FaEdit, FaTrash, FaSave, FaTimes, FaRobot, FaForward, FaVoicemail, FaRedo, FaHeadset } from 'react-icons/fa';

const Container = styled.div`
  padding: 20px;
  max-width: 900px;
`;

const PageTitle = styled.h1`
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
`;

const AddButton = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  font-size: 0.95rem;

  &:hover { opacity: 0.9; }
`;

const MenuCard = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  border-left: 4px solid ${props => {
    switch(props.actionType) {
      case 'ai_agent': return props.theme.colors.primary;
      case 'forward': return '#4caf50';
      case 'voicemail': return '#2196f3';
      case 'softphone': return '#9c27b0';
      case 'repeat': return '#9e9e9e';
      default: return props.theme.colors.surface;
    }
  }};
`;

const MenuInfo = styled.div`
  flex: 1;
`;

const Digit = styled.span`
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.primary};
  font-weight: bold;
  font-size: 1.2rem;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const MenuLabel = styled.div`
  color: ${props => props.theme.colors.textPrimary};
  font-weight: 500;
  margin-bottom: 4px;
`;

const MenuMeta = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.85rem;
`;

const ActionType = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 8px;
  border-radius: 4px;
  font-size: 0.8rem;
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.textSecondary};
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.color || props.theme.colors.textSecondary};
  cursor: pointer;
  font-size: 1rem;
  padding: 6px;

  &:hover { color: ${props => props.theme.colors.primary}; }
`;

const ActiveBadge = styled.span`
  font-size: 0.75rem;
  padding: 2px 8px;
  border-radius: 10px;
  background-color: ${props => props.active ? '#4caf5033' : '#fa7f7f33'};
  color: ${props => props.active ? '#4caf50' : '#fa7f7f'};
`;

// Form overlay
const FormOverlay = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
  border: 1px solid ${props => props.theme.colors.primary}44;
`;

const FormTitle = styled.h3`
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 16px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 80px 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const FormRow2 = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
  margin-bottom: 12px;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const Label = styled.label`
  display: block;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 4px;
  font-size: 0.85rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 8px 10px;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface};
  border-radius: 6px;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.9rem;
  box-sizing: border-box;

  &:focus { outline: none; border-color: ${props => props.theme.colors.primary}; }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface};
  border-radius: 6px;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.9rem;
  box-sizing: border-box;

  &:focus { outline: none; border-color: ${props => props.theme.colors.primary}; }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 8px;
`;

const Button = styled.button`
  padding: 8px 16px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 0.9rem;
  background-color: ${props => props.variant === 'cancel' ? props.theme.colors.background : props.theme.colors.primary};
  color: ${props => props.variant === 'cancel' ? props.theme.colors.textSecondary : 'white'};

  &:hover { opacity: 0.9; }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 40px;
  color: ${props => props.theme.colors.textSecondary};
`;

const actionTypeIcon = (type) => {
  switch(type) {
    case 'ai_agent': return <FaRobot />;
    case 'forward': return <FaForward />;
    case 'voicemail': return <FaVoicemail />;
    case 'softphone': return <FaHeadset />;
    case 'repeat': return <FaRedo />;
    default: return null;
  }
};

const actionTypeLabel = (type) => {
  switch(type) {
    case 'ai_agent': return 'AI Agent';
    case 'forward': return 'Forward Call';
    case 'voicemail': return 'Voicemail';
    case 'softphone': return 'Softphone (Browser)';
    case 'repeat': return 'Repeat Menu';
    default: return type;
  }
};

const emptyForm = { digit: '', label: '', tts_message: '', action_type: 'forward', action_target: '', is_active: true, fallback_target: null, dial_timeout: null };

const IvrMenuManager = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [showModal, setShowModal] = useState(false);
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => { loadItems(); }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const data = await getIvrMenu();
      setItems(data);
    } catch (err) {
      console.error('Error loading IVR menu:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = () => {
    setEditingId(null);
    setForm(emptyForm);
    setShowForm(true);
  };

  const handleEdit = (item) => {
    setEditingId(item.id);
    setForm({
      digit: item.digit,
      label: item.label,
      tts_message: item.tts_message,
      action_type: item.action_type,
      action_target: item.action_target || '',
      is_active: item.is_active,
      fallback_target: item.fallback_target || null,
      dial_timeout: item.dial_timeout || null,
    });
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
    setForm(emptyForm);
  };

  const handleSave = async () => {
    try {
      if (editingId) {
        await updateIvrMenuOption(editingId, form);
      } else {
        await createIvrMenuOption(form);
      }
      await loadItems();
      handleCancel();
    } catch (err) {
      console.error('Error saving IVR option:', err);
    }
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
    setShowModal(true);
  };

  const handleConfirmDelete = async () => {
    try {
      await deleteIvrMenuOption(deleteId);
      setItems(items.filter(i => i.id !== deleteId));
      setShowModal(false);
    } catch (err) {
      console.error('Error deleting IVR option:', err);
    }
  };

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return <DashboardLayout><Container><p style={{ color: '#ccc' }}>Loading...</p></Container></DashboardLayout>;
  }

  return (
    <DashboardLayout>
      <Container>
        <Header>
          <PageTitle><FaPhone /> IVR Phone Menu</PageTitle>
          <AddButton onClick={handleAdd}><FaPlus /> Add Option</AddButton>
        </Header>

        {showForm && (
          <FormOverlay>
            <FormTitle>{editingId ? 'Edit Menu Option' : 'New Menu Option'}</FormTitle>
            <FormRow>
              <div>
                <Label>Digit</Label>
                <Input maxLength={1} value={form.digit} onChange={e => handleChange('digit', e.target.value)} placeholder="1" />
              </div>
              <div>
                <Label>Label</Label>
                <Input value={form.label} onChange={e => handleChange('label', e.target.value)} placeholder="Request a Quote" />
              </div>
              <div>
                <Label>Action Type</Label>
                <Select value={form.action_type} onChange={e => handleChange('action_type', e.target.value)}>
                  <option value="forward">Forward Call</option>
                  <option value="ai_agent">AI Agent</option>
                  <option value="softphone">Softphone (Browser)</option>
                  <option value="voicemail">Voicemail</option>
                  <option value="repeat">Repeat Menu</option>
                </Select>
              </div>
            </FormRow>
            <div style={{ marginBottom: '12px' }}>
              <Label>TTS Message (what the caller hears)</Label>
              <Input value={form.tts_message} onChange={e => handleChange('tts_message', e.target.value)} placeholder="To request a quote, press 1." />
            </div>
            {(form.action_type === 'forward' || form.action_type === 'softphone') && (
              <div style={{ marginBottom: '12px' }}>
                <Label>{form.action_type === 'forward' ? 'Forward Number' : 'Client Identity (leave empty for default)'}</Label>
                <Input value={form.action_target} onChange={e => handleChange('action_target', e.target.value)} placeholder={form.action_type === 'forward' ? '+19786063330' : 'crm-softphone'} />
              </div>
            )}

            {form.action_type === 'ai_agent' && (() => {
              // Derive mode: null = direct, 'softphone' = softphone, anything else (including '') = phone
              const tryFirstMode = form.fallback_target === null ? 'direct' : form.fallback_target === 'softphone' ? 'softphone' : 'phone';

              return (
                <>
                  <FormRow2>
                    <div>
                      <Label>Try First (before AI Agent)</Label>
                      <Select value={tryFirstMode} onChange={e => {
                        const mode = e.target.value;
                        if (mode === 'direct') {
                          handleChange('fallback_target', null);
                          handleChange('dial_timeout', null);
                        } else if (mode === 'softphone') {
                          handleChange('fallback_target', 'softphone');
                          if (!form.dial_timeout) handleChange('dial_timeout', 24);
                        } else {
                          handleChange('fallback_target', '');
                          if (!form.dial_timeout) handleChange('dial_timeout', 24);
                        }
                      }}>
                        <option value="direct">Direct to AI Agent</option>
                        <option value="softphone">Softphone (Browser) first</option>
                        <option value="phone">Phone Number first</option>
                      </Select>
                    </div>
                    <div>
                      <Label>Ring Timeout</Label>
                      <Select value={form.dial_timeout || ''} onChange={e => handleChange('dial_timeout', e.target.value ? parseInt(e.target.value) : null)} disabled={tryFirstMode === 'direct'}>
                        <option value="">—</option>
                        <option value="18">3 rings (~18s)</option>
                        <option value="24">4 rings (~24s)</option>
                        <option value="30">5 rings (~30s)</option>
                        <option value="36">6 rings (~36s)</option>
                      </Select>
                    </div>
                  </FormRow2>
                  {tryFirstMode === 'phone' && (
                    <div style={{ marginBottom: '12px' }}>
                      <Label>Phone Number to try first</Label>
                      <Input value={form.fallback_target || ''} onChange={e => handleChange('fallback_target', e.target.value)} placeholder="+15082944541" />
                    </div>
                  )}
                </>
              );
            })()}

            <FormRow2>
              <div>
                <Label>Active</Label>
                <Select value={form.is_active ? 'true' : 'false'} onChange={e => handleChange('is_active', e.target.value === 'true')}>
                  <option value="true">Active</option>
                  <option value="false">Inactive</option>
                </Select>
              </div>
              <div></div>
            </FormRow2>
            <ButtonGroup>
              <Button onClick={handleSave}><FaSave /> Save</Button>
              <Button variant="cancel" onClick={handleCancel}><FaTimes /> Cancel</Button>
            </ButtonGroup>
          </FormOverlay>
        )}

        {items.length === 0 ? (
          <EmptyState>No IVR menu options configured. Click "Add Option" to create the phone menu.</EmptyState>
        ) : (
          items.map(item => (
            <MenuCard key={item.id} actionType={item.action_type}>
              <Digit>{item.digit}</Digit>
              <MenuInfo>
                <MenuLabel>{item.label}</MenuLabel>
                <MenuMeta>
                  "{item.tts_message}"
                </MenuMeta>
                <div style={{ marginTop: '4px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <ActionType>{actionTypeIcon(item.action_type)} {actionTypeLabel(item.action_type)}</ActionType>
                  {item.action_target && <MenuMeta>{item.action_target}</MenuMeta>}
                  {item.action_type === 'ai_agent' && item.fallback_target && (
                    <MenuMeta style={{ color: '#F69620' }}>
                      Try {item.fallback_target === 'softphone' ? 'Softphone' : item.fallback_target} first ({item.dial_timeout}s)
                    </MenuMeta>
                  )}
                  <ActiveBadge active={item.is_active}>{item.is_active ? 'Active' : 'Inactive'}</ActiveBadge>
                </div>
              </MenuInfo>
              <div style={{ display: 'flex', gap: '4px' }}>
                <IconButton onClick={() => handleEdit(item)}><FaEdit /></IconButton>
                <IconButton onClick={() => handleDeleteClick(item.id)} color="#fa7f7f"><FaTrash /></IconButton>
              </div>
            </MenuCard>
          ))
        )}

        {showModal && (
          <Modal
            isOpen={showModal}
            title="Delete Menu Option"
            message="Are you sure you want to delete this IVR menu option?"
            onConfirm={handleConfirmDelete}
            onCancel={() => setShowModal(false)}
          />
        )}
      </Container>
    </DashboardLayout>
  );
};

export default IvrMenuManager;
